import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { SearchInput } from "../../../components/ui/search-input";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { Plus, ChevronLeft, ChevronRight, Trash2, Edit2, Tags } from "lucide-react";
import { Loader } from "../../../components/Loader";
import { normalizeSearchQuery } from "../../../utils/search";

type CategoryFormData = {
  id: string;
  name: string;
};

export const CategoriesPage = () => {
  const [categories, setCategories] = useState<any[]>([]);
  const [totalCategories, setTotalCategories] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [formData, setFormData] = useState<CategoryFormData>({
    id: "",
    name: "",
  });
  const [submitError, setSubmitError] = useState("");

  const { authToken = "", id = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  const maxResults = 12;

  const fetchCategories = async (pageNum = 1, search = "") => {
    try {
      const firstResults = (pageNum - 1) * maxResults;
      const params: Record<string, unknown> = { firstResults, maxResults };
      if (search.trim()) params.search = search.trim();
      const response = await axios.get(`${API_BASE_URL}account/${id}/categories`, {
        params,
        headers: { Authorization: `Bearer ${authToken}` },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching categories:", error);
      return { categories: [], totalCount: 0 };
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError("");
    try {
      if (formData.id) {
        await axios.put(
          `${API_BASE_URL}account/${id}/category/update`,
          { id: Number(formData.id), name: formData.name.trim() },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}account/${id}/category/create`,
          { name: formData.name.trim() },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }
      const result = await fetchCategories(page, appliedSearch);
      setCategories(result.categories || []);
      setTotalCategories(result.totalCount || 0);
      setIsDrawerOpen(false);
      resetForm();
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        "Failed to save category. Please try again.";
      setSubmitError(msg);
      console.error("Failed to submit category:", error);
    }
  };

  const handleDelete = async (categoryId: string | number) => {
    if (!window.confirm("Are you sure you want to delete this category?")) return;
    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/category/${categoryId}/delete`,
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setCategories(categories.filter((c) => c.id !== categoryId));
      setTotalCategories((n) => Math.max(0, n - 1));
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        "Failed to delete category. Please try again.";
      window.alert(msg);
      console.error("Failed to delete category:", error);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const result = await fetchCategories(page, appliedSearch);
        setCategories(result.categories || []);
        setTotalCategories(result.totalCount || 0);
      } finally {
        setLoading(false);
      }
    };
    if (id && authToken) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, appliedSearch, id, authToken]);

  const openDrawerForCreate = () => {
    setFormData({ id: "", name: "" });
    setSubmitError("");
    setIsCreating(true);
    setIsDrawerOpen(true);
  };

  const openDrawerForEdit = (category: any) => {
    setFormData({
      id: category?.id != null ? String(category.id) : "",
      name: category?.name ?? "",
    });
    setSubmitError("");
    setIsCreating(false);
    setIsDrawerOpen(true);
  };

  const resetForm = () => {
    setFormData({ id: "", name: "" });
    setSubmitError("");
  };

  const totalPages = Math.max(1, Math.ceil(totalCategories / maxResults));

  if (loading && categories.length === 0) {
    return <Loader />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Tags className="text-blue-600" size={28} />
            Product Categories
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Organize products into categories for order picking
          </p>
        </div>
        <Button
          onClick={openDrawerForCreate}
          className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 w-full sm:w-auto"
        >
          <Plus size={18} />
          New Category
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <SearchInput
          value={searchTerm}
          onValueChange={setSearchTerm}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              setPage(1);
              setAppliedSearch(normalizeSearchQuery(searchTerm));
            }
          }}
          placeholder="Search categories..."
          className="w-full"
        />
        <div className="mt-2 flex gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              setPage(1);
              setAppliedSearch(normalizeSearchQuery(searchTerm));
            }}
          >
            Search
          </Button>
          {appliedSearch ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setSearchTerm("");
                setAppliedSearch("");
                setPage(1);
              }}
            >
              Clear
            </Button>
          ) : null}
        </div>
      </div>

      {categories.length === 0 ? (
        <div className="py-12 text-center bg-white rounded-lg border border-gray-200">
          <Tags className="mx-auto mb-4 text-gray-300" size={48} />
          <p className="text-gray-600 font-medium">No categories found</p>
          <p className="text-gray-400 text-sm mt-1">
            Create a category to assign products
          </p>
        </div>
      ) : (
        <>
          <div className="hidden md:block bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">
                    Name
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {categories.map((category) => (
                  <tr key={category.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {category.name}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="inline-flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => openDrawerForEdit(category)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                        >
                          <Edit2 size={14} className="mr-1" />
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(category.id)}
                          className="text-red-600 border-red-200 hover:bg-red-50"
                        >
                          <Trash2 size={14} className="mr-1" />
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-3">
            {categories.map((category) => (
              <div
                key={category.id}
                className="bg-white border border-gray-200 rounded-lg p-4 shadow-sm"
              >
                <h3 className="font-semibold text-gray-900 mb-3">{category.name}</h3>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => openDrawerForEdit(category)}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    <Edit2 size={14} className="mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(category.id)}
                    className="flex-1 text-red-600 border-red-200 hover:bg-red-50"
                  >
                    <Trash2 size={14} className="mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 p-4 bg-white rounded-lg border border-gray-200">
          <p className="text-sm text-gray-600">
            Page <strong>{page}</strong> of <strong>{totalPages}</strong> (
            <strong>{totalCategories}</strong> total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <ChevronLeft size={18} />
              <span className="hidden sm:inline">Previous</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="flex items-center gap-2 disabled:opacity-50"
            >
              <span className="hidden sm:inline">Next</span>
              <ChevronRight size={18} />
            </Button>
          </div>
        </div>
      )}

      {isDrawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsDrawerOpen(false)}
        />
      )}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-96 md:h-auto max-h-screen md:max-h-[90vh] overflow-y-auto rounded-t-2xl md:rounded-lg ${
          isDrawerOpen
            ? "translate-y-0 md:scale-100"
            : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-900">
            {isCreating ? "Create Category" : "Edit Category"}
          </h2>
          <button
            onClick={() => setIsDrawerOpen(false)}
            className="text-gray-500 hover:text-gray-700 text-2xl"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <Label className="text-gray-700 mb-2 block">Category Name</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g. Curtains, Blinds"
              className="w-full"
              required
            />
          </div>
          {submitError ? (
            <p className="text-sm text-red-600">{submitError}</p>
          ) : null}
          <div className="flex gap-2 pt-4">
            <Button
              type="submit"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isCreating ? "Create" : "Update"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsDrawerOpen(false);
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoriesPage;
